import { useEffect, useRef, useState } from "react"

import { CollectionItem } from "@vertesia/common"
import { BotMessageSquare, EllipsisVertical, CopyIcon, ListCollapse, Pen, Share2, SquarePen, PanelRightOpen } from "lucide-react"
import { toast } from "sonner"

import { ActionTooltipButton } from "@components/buttons/ActionTooltipButton"
import { AgentRunnerOverlay } from "@components/overlays/AgentRunnerOverlay"

import { useWatchSearchResult } from "@plugins/providers/agent/AgentHistoryContext"

import { useUserSession } from "@session/UserSession"

import { Badge } from "@ui/badge"
import { Button } from "@ui/button"
import { Input } from "@ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@ui/popover"
import { Separator } from "@ui/separator"

interface HeaderProps {
    agentMode?: string
    runId?: string
    chatId?: string
    collection?: CollectionItem
    viewMode: "agent" | "stacked"
    onViewModeChange: (mode: "agent" | "stacked") => void
    onCopyRunId?: () => void
    onCopyChatId?: () => void
    onNewChat?: () => void
    onNameChange?: (name: string) => void
    onShare?: () => void
}
export function Header({ agentMode, runId, chatId, collection, viewMode, onViewModeChange, onCopyRunId, onCopyChatId, onNewChat, onNameChange, onShare }: Readonly<HeaderProps>) {
    const { client } = useUserSession()
    const { search, runs } = useWatchSearchResult()

    const inputRef = useRef<HTMLInputElement>(null)

    const [open, setOpen] = useState(false)

    const [rename, setRename] = useState(false)
    const [name, setName] = useState(collection?.name)

    const handleOpenChange = (newOpen: boolean) => {
        if (!newOpen && rename) {
            return
        }
        setOpen(newOpen)
    }

    const handleUpdateCollection = async () => {
        if (!collection || !name) {
            return
        }

        const payload = {
            name: name,
            properties: (collection as any).properties,
        }

        client.store.collections.update(collection.id, payload).then(() => {
            toast.success("Agent Task Name updated successfully", { duration: 2000 })
            setRename(false)
            onNameChange?.(name)
            setOpen(false)
        })
    }

    const handleOnRename = () => {
        setName(collection?.name)
        setRename(true)
    }

    const handleRenameReset = () => {
        setName(collection?.name)
        setRename(false)
    }

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape" && rename) {
                e.stopPropagation()
                e.preventDefault()
                e.stopImmediatePropagation()
                handleRenameReset()
            } else if (e.key === "Enter" && rename) {
                e.stopPropagation()
                e.preventDefault()
                handleUpdateCollection()
            }
        }

        const inputElement = inputRef.current
        if (inputElement && rename) {
            inputElement.addEventListener("keydown", handleKeyDown, true)
            return () => {
                inputElement.removeEventListener("keydown", handleKeyDown, true)
            }
        }
    }, [rename, name, collection?.name])

    useEffect(() => {
        if (runs.length) {
            return
        }
        search.search()
    }, [])

    useEffect(() => {
        if (collection) {
            setName(collection.name)
        }
    }, [collection])

    return (
        <div className="absolute z-10 flex flex-row right-0 items-center gap-2 justify-end p-2">
            {(runId || chatId) && (
                <>
                    {agentMode && (
                        <Badge variant="outline" className="bg-white">
                            {agentMode}
                        </Badge>
                    )}
                    <div>
                        <ActionTooltipButton variant="ghost" icon={<SquarePen />} tooltip="Start new chat" onClick={onNewChat} />
                    </div>
                    <Popover open={open} onOpenChange={handleOpenChange}>
                        <PopoverTrigger asChild>
                            <Button size="icon" variant="ghost">
                                <EllipsisVertical />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent side="bottom" align="end" className="p-1 flex flex-col gap-1">
                            {chatId && (
                                <Button variant="ghost" onClick={onCopyChatId} className="text-left justify-start w-full text-muted-foreground">
                                    <CopyIcon /> Copy Chat ID
                                </Button>
                            )}
                            {runId && (
                                <Button variant="ghost" onClick={onCopyRunId} className="text-left justify-start w-full text-muted-foreground">
                                    <CopyIcon /> Copy Run ID
                                </Button>
                            )}
                            <Button variant="ghost" onClick={onShare} className="text-left justify-start w-full text-muted-foreground">
                                <Share2 /> Share
                            </Button>
                            {collection && (
                                <>
                                    {rename ? (
                                        <Input ref={inputRef} type="text" className="w-full bg-transparent border-b border-gray-300 focus:border-blue-500 focus:outline-none" defaultValue={collection.name} onChange={(e) => setName(e.target.value)} onBlur={handleRenameReset} autoFocus />
                                    ) : (
                                        <Button variant="ghost" onClick={handleOnRename} className="text-left justify-start w-full text-muted-foreground">
                                            <Pen /> Rename
                                        </Button>
                                    )}
                                </>
                            )}
                            <Separator />
                            <Button variant="ghost" onClick={() => onViewModeChange("agent")} className={`text-left justify-start w-full text-muted-foreground ${viewMode === "agent" ? "bg-gray-100 text-primary" : ""}`}>
                                <BotMessageSquare /> Chat View
                            </Button>
                            <Button variant="ghost" onClick={() => onViewModeChange("stacked")} className={`text-left justify-start w-full text-muted-foreground ${viewMode === "stacked" ? "bg-gray-100 text-primary" : ""}`}>
                                <ListCollapse /> Details View
                            </Button>
                        </PopoverContent>
                    </Popover>
                </>
            )}
            <AgentRunnerOverlay>
                <ActionTooltipButton variant="ghost" icon={<PanelRightOpen />} tooltip="Display Previous Chat" onClick={() => {}} />
            </AgentRunnerOverlay>
        </div>
    )
}
